import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'

import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface NoteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  confirmLabel: string
  required?: boolean
  destructive?: boolean
  isPending?: boolean
  children?: React.ReactNode
  onConfirm: (note: string) => void
}

/** Ventana para pedir, rechazar o cancelar con una nota. */
export function NoteDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  required = false,
  destructive = false,
  isPending = false,
  children,
  onConfirm,
}: NoteDialogProps) {
  const [note, setNote] = useState('')

  useEffect(() => {
    if (open) setNote('')
  }, [open])

  const canConfirm = !isPending && (!required || note.trim().length > 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {/* min-w-0: dentro de la grilla del diálogo, un texto truncado lo ensanchaba en el celular */}
        {children && <div className="min-w-0">{children}</div>}
        <div className="space-y-2 min-w-0">
          <Label htmlFor="approval-note">
            {required ? t('approvals.note') : t('approvals.noteOptional')}
          </Label>
          <Textarea
            id="approval-note"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            placeholder={required ? t('approvals.noteRequired') : undefined}
            rows={3}
          />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-11 sm:h-10" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('approvals.back')}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            className="h-11 sm:h-10"
            disabled={!canConfirm}
            onClick={() => onConfirm(note.trim())}
          >
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
